/*
 * Laser engraving - intercepts add-to-cart for products in the
 * 'laser-engraving-eligible' collection and opens a product-specific popup.
 *
 * Per-accessory settings come from product metafields (read in theme.liquid into
 * window.engravingConfig.products[productId]):
 *   feeVariantId / feeDisplay  (custom.engraving_fee_product)
 *   instructions               (custom.engraving_instructions)
 *   allowText / allowLogo      (custom.engraving_allow_text / _allow_logo)  - text/logo tabs
 *   combined                   (custom.engraving_combined)   - show text AND logo together
 *   maxChars                   (custom.engraving_max_chars)
 *   mainLabel                  (custom.engraving_main_label)   - label of the main input
 *   field2Label                (custom.engraving_field2_label) - optional 2nd text field
 *   positions                  (custom.engraving_positions)    - comma list -> position picker
 * Anything unset falls back to sensible defaults.
 *
 * Product is added as a normal line item; the personalisation (main text and/or
 * logo file, plus the optional 2nd field and position) is attached to the SEPARATE
 * engraving fee line item so it travels with the fee to checkout/fulfilment.
 */
class LaserEngraving {
  constructor() {
    this.modal = document.getElementById('engraving-modal');
    if (!this.modal) return;

    this.config = window.engravingConfig || {};
    this.textInput = document.getElementById('engraving-text');
    this.mainLabelEl = document.getElementById('engraving-main-label');
    this.fileInput = document.getElementById('engraving-file');
    this.fileMeta = document.getElementById('engraving-file-meta');
    this.tabs = Array.prototype.slice.call(this.modal.querySelectorAll('[data-engraving-mode]'));
    this.tabsBar = this.modal.querySelector('.engraving-modal__tabs');
    this.panelText = document.getElementById('engraving-panel-text');
    this.panelLogo = document.getElementById('engraving-panel-logo');
    this.field2El = document.getElementById('engraving-field2');
    this.field2LabelEl = document.getElementById('engraving-field2-label');
    this.field2InputEl = document.getElementById('engraving-field2-input');
    this.positionsEl = document.getElementById('engraving-positions');
    this.posListEl = document.getElementById('engraving-pos-list');
    this.confirmBtn = document.getElementById('engraving-confirm');
    this.skipBtn = document.getElementById('engraving-skip');
    this.closeBtn = document.getElementById('engraving-modal-close');
    this.errorEl = document.getElementById('engraving-error');
    this.loadingEl = document.getElementById('engraving-loading');
    this.productNameEl = document.getElementById('engraving-modal-product-name');
    this.subtitleEl = this.modal.querySelector('.engraving-modal__subtitle');
    this.priceEl = document.getElementById('engraving-price');
    this.defaultSubtitle = this.subtitleEl ? this.subtitleEl.textContent : '';
    this.defaultMainLabel = this.mainLabelEl ? this.mainLabelEl.textContent : 'Text to engrave';

    this.mode = 'text';
    this.combined = false;
    this.file = null;
    this.pending = null;
    this.currentCfg = {};
    this.selectedPosition = '';
    this.capturedForm = null;
    this.maxBytes = 20 * 1024 * 1024;
    this.init();
  }

  init() {
    document.addEventListener('click', this.onButtonClick.bind(this), true);
    this.textInput.addEventListener('input', this.refreshConfirm.bind(this));
    this.fileInput.addEventListener('change', this.onFileChange.bind(this));
    this.tabs.forEach((t) => t.addEventListener('click', () => this.setMode(t.dataset.engravingMode)));
    this.confirmBtn.addEventListener('click', this.onConfirm.bind(this));
    this.skipBtn.addEventListener('click', this.onSkip.bind(this));
    this.closeBtn.addEventListener('click', () => this.closeModal());
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.closeModal(); });
  }

  isEligibleVariant(id) { var vp = this.config.variantToProduct || {}; return id != null && Object.prototype.hasOwnProperty.call(vp, String(id)); }
  isEligibleProduct(id) { var p = this.config.products || {}; return !!id && Object.prototype.hasOwnProperty.call(p, String(id)); }
  resolveProductId(variantId, productId) {
    var products = this.config.products || {};
    if (productId && products[String(productId)]) return String(productId);
    var vp = this.config.variantToProduct || {};
    if (variantId != null && vp[String(variantId)] != null) return String(vp[String(variantId)]);
    return productId ? String(productId) : null;
  }
  cfgFor(productId) { return (this.config.products && this.config.products[String(productId)]) || {}; }

  onButtonClick(e) {
    const trigger = e.target.closest('[data-engraving-trigger]');
    if (trigger) {
      e.preventDefault(); e.stopImmediatePropagation();
      this.capturedForm = null;
      const pid = this.resolveProductId(trigger.dataset.variantId, trigger.dataset.productId);
      this.pending = { variantId: trigger.dataset.variantId, quantity: parseInt(trigger.dataset.quantity || '1', 10), productId: pid, productTitle: this.cfgFor(pid).title || trigger.dataset.productTitle || '' };
      this.openModal();
      return;
    }
    const btn = e.target.closest('button[name="add"]');
    if (btn) {
      const form = btn.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;
      const variantId = String(variantInput.value);
      const formComponent = form.closest('[data-product-id]');
      const productId = formComponent?.dataset.productId;
      const eligibleAncestor = form.closest('[data-engraving-eligible]');
      const eligible = this.isEligibleVariant(variantId) || this.isEligibleProduct(productId) || !!eligibleAncestor;
      if (!eligible) return;
      e.preventDefault(); e.stopImmediatePropagation();
      const pid = this.resolveProductId(variantId, productId);
      this.capturedForm = form;
      this.pending = { variantId, quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10), productId: pid, productTitle: this.cfgFor(pid).title || eligibleAncestor?.dataset.productTitle || '' };
      this.openModal();
      return;
    }
    const carouselBtn = e.target.closest('button[data-variant-id][data-product-id]');
    if (carouselBtn && !carouselBtn.disabled) {
      const variantId = String(carouselBtn.dataset.variantId);
      const productId = String(carouselBtn.dataset.productId);
      if (!this.isEligibleVariant(variantId) && !this.isEligibleProduct(productId)) return;
      e.preventDefault(); e.stopImmediatePropagation();
      const pid = this.resolveProductId(variantId, productId);
      const title = this.cfgFor(pid).title || carouselBtn.closest('[class*="card"]')?.querySelector('h2, h3, h4')?.textContent?.trim() || '';
      this.capturedForm = null;
      this.pending = { variantId, quantity: 1, productId: pid, productTitle: title };
      this.openModal();
      return;
    }
    const stickyBtn = e.target.closest('.sticky-add-to-cart__button');
    if (stickyBtn && !stickyBtn.disabled) {
      const stickyEl = stickyBtn.closest('sticky-add-to-cart');
      const variantId = stickyEl?.dataset.currentVariantId;
      const productId = stickyEl?.dataset.productId;
      if (!variantId || !productId) return;
      if (!this.isEligibleVariant(variantId) && !this.isEligibleProduct(productId)) return;
      e.preventDefault(); e.stopImmediatePropagation();
      const pid = this.resolveProductId(variantId, productId);
      this.capturedForm = null;
      this.pending = { variantId: String(variantId), quantity: 1, productId: pid, productTitle: this.cfgFor(pid).title || '' };
      this.openModal();
      return;
    }
    const payBtn = e.target.closest('.shopify-payment-button__button');
    if (payBtn && !payBtn.disabled) {
      const form = payBtn.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;
      const variantId = String(variantInput.value);
      const formComponent = form.closest('[data-product-id]');
      const productId = formComponent?.dataset.productId;
      const eligibleAncestor = form.closest('[data-engraving-eligible]');
      const eligible = this.isEligibleVariant(variantId) || this.isEligibleProduct(productId) || !!eligibleAncestor;
      if (!eligible) return;
      e.preventDefault(); e.stopImmediatePropagation();
      const pid = this.resolveProductId(variantId, productId);
      this.capturedForm = null;
      this.pending = { variantId, quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10), productId: pid, productTitle: this.cfgFor(pid).title || '', goToCheckout: true };
      this.openModal();
    }
  }

  openModal() {
    const cfg = this.cfgFor(this.pending && this.pending.productId);
    this.currentCfg = cfg;

    this.textInput.value = '';
    this.fileInput.value = '';
    this.file = null;
    this.fileMeta.innerHTML = '';

    if (this.subtitleEl) this.subtitleEl.textContent = cfg.instructions && cfg.instructions.length ? cfg.instructions : this.defaultSubtitle;
    if (this.mainLabelEl) this.mainLabelEl.textContent = cfg.mainLabel && cfg.mainLabel.length ? cfg.mainLabel : this.defaultMainLabel;
    if (this.priceEl) this.priceEl.textContent = cfg.feeDisplay || this.config.defaultFeeDisplay || this.priceEl.textContent;
    this.textInput.maxLength = cfg.maxChars || 120;

    if (this.field2El) {
      if (cfg.field2Label && cfg.field2Label.length) {
        if (this.field2LabelEl) this.field2LabelEl.textContent = cfg.field2Label;
        if (this.field2InputEl) this.field2InputEl.value = '';
        this.field2El.style.display = '';
      } else {
        this.field2El.style.display = 'none';
      }
    }

    this.selectedPosition = '';
    if (this.positionsEl && this.posListEl) {
      const posStr = (cfg.positions || '').trim();
      if (posStr) {
        const arr = posStr.split(',').map((x) => x.trim()).filter(Boolean);
        this.posListEl.innerHTML = '';
        const self = this;
        arr.forEach((p, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'engraving-modal__pos' + (i === 0 ? ' is-active' : '');
          b.textContent = p;
          b.addEventListener('click', () => {
            self.posListEl.querySelectorAll('.engraving-modal__pos').forEach((x) => x.classList.remove('is-active'));
            b.classList.add('is-active');
            self.selectedPosition = p;
          });
          self.posListEl.appendChild(b);
        });
        this.selectedPosition = arr[0] || '';
        this.positionsEl.style.display = '';
      } else {
        this.positionsEl.style.display = 'none';
      }
    }

    this.combined = cfg.combined === true;
    if (this.combined) {
      this.tabs.forEach((t) => { t.style.display = 'none'; });
      if (this.tabsBar) this.tabsBar.style.display = 'none';
      this.panelText.classList.remove('hidden');
      this.panelLogo.classList.remove('hidden');
      this.mode = 'combined';
      this.refreshConfirm();
    } else {
      let allowText = cfg.allowText !== false;
      let allowLogo = cfg.allowLogo !== false;
      if (!allowText && !allowLogo) allowText = true;
      this.tabs.forEach((t) => {
        const m = t.dataset.engravingMode;
        t.style.display = ((m === 'text' && allowText) || (m === 'logo' && allowLogo)) ? '' : 'none';
      });
      if (this.tabsBar) this.tabsBar.style.display = (allowText && allowLogo) ? '' : 'none';
      this.setMode(allowText ? 'text' : 'logo');
    }

    this.productNameEl.textContent = (this.pending && this.pending.productTitle) || '';
    this.errorEl.classList.add('hidden');
    this.loadingEl.classList.add('hidden');
    this.setLoading(false);
    this.modal.showModal();
    requestAnimationFrame(() => { if (!this.panelText.classList.contains('hidden')) this.textInput.focus(); });
  }

  closeModal() { this.modal.close(); this.pending = null; this.capturedForm = null; }

  setMode(mode) {
    this.mode = mode;
    this.tabs.forEach((t) => t.classList.toggle('is-active', t.dataset.engravingMode === mode));
    this.panelText.classList.toggle('hidden', mode !== 'text');
    this.panelLogo.classList.toggle('hidden', mode !== 'logo');
    this.refreshConfirm();
  }

  onFileChange() {
    this.errorEl.classList.add('hidden');
    const f = this.fileInput.files && this.fileInput.files[0];
    this.file = null;
    this.fileMeta.innerHTML = '';
    if (!f) { this.refreshConfirm(); return; }
    const okType = /(^image\/(svg\+xml|png|jpeg)$)/.test(f.type) || /\.(svg|png|jpe?g)$/i.test(f.name);
    if (!okType) { this.showError('Please upload an SVG, PNG or JPEG file.'); this.fileInput.value = ''; this.refreshConfirm(); return; }
    if (f.size > this.maxBytes) { this.showError('That file is over 20 MB. Please upload a smaller file.'); this.fileInput.value = ''; this.refreshConfirm(); return; }
    this.file = f;
    const sizeKb = Math.round(f.size / 1024);
    this.fileMeta.textContent = f.name + ' · ' + (sizeKb >= 1024 ? (sizeKb / 1024).toFixed(1) + ' MB' : sizeKb + ' KB');
    if (/(png|jpe?g)$/i.test(f.name) || /image\/(png|jpeg)/.test(f.type)) {
      const img = document.createElement('img');
      img.alt = 'Logo preview';
      img.src = URL.createObjectURL(f);
      this.fileMeta.appendChild(img);
    }
    this.refreshConfirm();
  }

  refreshConfirm() {
    let ready = false;
    if (this.combined) ready = this.textInput.value.trim().length > 0 || !!this.file;
    else if (this.mode === 'text') ready = this.textInput.value.trim().length > 0;
    else ready = !!this.file;
    this.confirmBtn.disabled = !ready;
  }

  showError(msg) { this.errorEl.textContent = msg; this.errorEl.classList.remove('hidden'); }

  onSkip() {
    const form = this.capturedForm;
    const pending = this.pending;
    this.closeModal();
    if (form) form.requestSubmit();
    else if (pending?.goToCheckout && pending?.variantId) this.skipBuyNow(String(pending.variantId), pending.quantity || 1);
    else if (pending?.variantId) this.skipDirectAdd(String(pending.variantId), pending.quantity || 1);
  }

  async skipDirectAdd(variantId, quantity) {
    try {
      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity }] }) });
      if (res.ok) await this.refreshCart();
    } catch (e) {}
  }
  async skipBuyNow(variantId, quantity) {
    try { await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: variantId, quantity }] }) }); } catch (e) {}
    window.location.href = '/checkout';
  }

  extraProps() {
    const extra = {};
    if (this.field2El && this.field2El.style.display !== 'none' && this.field2InputEl && this.field2InputEl.value.trim()) {
      extra[this.currentCfg.field2Label || 'Detail'] = this.field2InputEl.value.trim();
    }
    if (this.selectedPosition) extra['Position'] = this.selectedPosition;
    return extra;
  }

  async onConfirm() {
    if (!this.pending) return;
    const goToCheckout = !!this.pending.goToCheckout;
    const feeVariantId = (this.currentCfg && this.currentCfg.feeVariantId) || this.config.defaultFeeVariantId;
    const mainKey = (this.currentCfg && this.currentCfg.mainLabel && this.currentCfg.mainLabel.length) ? this.currentCfg.mainLabel : 'Engraving text';
    const extra = this.extraProps();
    const text = this.textInput.value.trim();
    const hasFile = !!this.file;
    const useFile = (this.mode === 'logo') || (this.combined && hasFile);
    this.setLoading(true);
    this.errorEl.classList.add('hidden');

    try {
      if (useFile) {
        if (!hasFile) { this.setLoading(false); return; }
        const pRes = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.pending.variantId, quantity: this.pending.quantity }] }) });
        if (!pRes.ok) throw new Error();
        if (feeVariantId) {
          const fd = new FormData();
          fd.append('id', feeVariantId);
          fd.append('quantity', '1');
          fd.append('properties[For]', this.pending.productTitle);
          fd.append('properties[Engraving]', (this.combined && text) ? 'Text + logo' : 'Logo upload');
          fd.append('properties[Engraving logo]', this.file, this.file.name);
          if (this.combined && text) fd.append('properties[' + mainKey + ']', text);
          Object.keys(extra).forEach((k) => fd.append('properties[' + k + ']', extra[k]));
          const fRes = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
          if (!fRes.ok) throw new Error();
        }
      } else {
        if (!text) { this.setLoading(false); return; }
        const items = [{ id: this.pending.variantId, quantity: this.pending.quantity }];
        if (feeVariantId) {
          const props = { 'For': this.pending.productTitle };
          props[mainKey] = text;
          Object.keys(extra).forEach((k) => { props[k] = extra[k]; });
          items.push({ id: feeVariantId, quantity: 1, properties: props });
        }
        const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!res.ok) throw new Error();
      }

      this.closeModal();
      if (goToCheckout) window.location.href = '/checkout';
      else await this.refreshCart();
    } catch (e) {
      this.setLoading(false);
      this.showError('Something went wrong adding it to your cart. Please try again.');
    }
  }

  async refreshCart() {
    try {
      const res = await fetch('/?sections=cart-icon-bubble,cart-drawer');
      const sections = await res.json();
      if (sections['cart-icon-bubble']) { const el = document.getElementById('cart-icon-bubble'); if (el) el.innerHTML = sections['cart-icon-bubble']; }
      if (sections['cart-drawer']) { const el = document.getElementById('CartDrawer'); if (el) el.innerHTML = sections['cart-drawer']; }
    } catch (e) {}
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
    document.querySelector('#cart-icon-bubble a')?.click();
  }

  setLoading(on) {
    this.confirmBtn.disabled = on;
    this.skipBtn.disabled = on;
    this.loadingEl.classList.toggle('hidden', !on);
    if (!on) this.refreshConfirm();
  }
}

document.addEventListener('DOMContentLoaded', () => new LaserEngraving());
